import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Loader2, 
  Shield,
  AlertTriangle,
  FileWarning,
  CheckCircle2,
  XCircle,
  Eye,
  Clock,
  TrendingUp,
  Ban,
  RefreshCw,
  FileText,
  Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('fleetflow_token')}`,
  'Content-Type': 'application/json'
});

const AMLDashboardPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [pendingSars, setPendingSars] = useState([]);
  const [selectedSar, setSelectedSar] = useState(null);
  const [showSarModal, setShowSarModal] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load AML dashboard
      const dashRes = await fetch(`${API_URL}/api/aml/dashboard?period_days=30`, {
        headers: getHeaders()
      });
      if (dashRes.ok) {
        setDashboard(await dashRes.json());
      }
      
      // Load pending SARs
      const sarRes = await fetch(`${API_URL}/api/aml/sars/pending`, {
        headers: getHeaders()
      });
      if (sarRes.ok) {
        const sarData = await sarRes.json();
        setPendingSars(sarData.sars || []);
      }
      
    } catch (error) {
      console.error('Error loading AML data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmitSar = async (sarId) => {
    try {
      const res = await fetch(`${API_URL}/api/aml/sars/${sarId}/submit`, {
        method: 'POST',
        headers: getHeaders()
      });
      
      if (res.ok) {
        toast.success('SAR soumis avec succès');
        setShowSarModal(false);
        loadData();
      } else {
        toast.error('Erreur lors de la soumission');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  const getRiskBadge = (category) => {
    const styles = {
      low: 'bg-emerald-100 text-emerald-700',
      medium: 'bg-amber-100 text-amber-700',
      high: 'bg-orange-100 text-orange-700',
      prohibited: 'bg-red-100 text-red-700'
    };
    const labels = {
      low: 'Faible',
      medium: 'Moyen',
      high: 'Élevé',
      prohibited: 'Interdit'
    };
    return <Badge className={styles[category] || styles.medium}>{labels[category] || category}</Badge>;
  };

  const getAlertIcon = (type) => {
    if (type?.includes('threshold')) return <TrendingUp className="w-4 h-4 text-orange-500" />;
    if (type?.includes('structuring')) return <Ban className="w-4 h-4 text-red-500" />;
    if (type?.includes('velocity') || type?.includes('rapid')) return <Clock className="w-4 h-4 text-amber-500" />;
    return <AlertTriangle className="w-4 h-4 text-slate-500" />;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Chivo'] tracking-tight flex items-center gap-2">
              <Shield className="w-7 h-7 text-orange-500" />
              Conformité AML
            </h1>
            <p className="text-slate-600 mt-1">Surveillance anti-blanchiment et rapports d&apos;activités suspectes</p>
          </div>
          <Button onClick={loadData} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Screenings (30j)</p>
                  <p className="text-2xl font-bold font-['JetBrains_Mono'] text-slate-900">
                    {dashboard?.screenings?.total || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Haut risque</p>
                  <p className="text-2xl font-bold font-['JetBrains_Mono'] text-orange-600">
                    {dashboard?.screenings?.high_risk || 0}
                  </p>
                  <p className="text-xs text-slate-500">{dashboard?.screenings?.high_risk_rate || 0}%</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Bloqués</p>
                  <p className="text-2xl font-bold font-['JetBrains_Mono'] text-red-600">
                    {dashboard?.screenings?.blocked || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <Ban className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">SAR en attente</p>
                  <p className="text-2xl font-bold font-['JetBrains_Mono'] text-slate-900">
                    {dashboard?.sars?.pending || 0}
                  </p>
                </div>
                <div className={`w-12 h-12 ${(dashboard?.sars?.pending || 0) > 0 ? 'bg-amber-100' : 'bg-slate-100'} rounded-lg flex items-center justify-center`}>
                  <FileWarning className={`w-6 h-6 ${(dashboard?.sars?.pending || 0) > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 max-w-xl gap-1">
            <TabsTrigger value="overview" className="text-xs md:text-sm">
              <TrendingUp className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Aperçu</span>
            </TabsTrigger>
            <TabsTrigger value="sars" className="text-xs md:text-sm">
              <FileWarning className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">SAR</span> ({pendingSars.length})
            </TabsTrigger>
            <TabsTrigger value="thresholds" className="text-xs md:text-sm">
              <Shield className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Seuils</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Alert Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Distribution des alertes</CardTitle>
                  <CardDescription>Types d&apos;alertes déclenchées sur les 30 derniers jours</CardDescription>
                </CardHeader>
                <CardContent>
                  {dashboard?.alert_distribution && Object.keys(dashboard.alert_distribution).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(dashboard.alert_distribution).map(([type, count]) => (
                        <div key={type} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            {getAlertIcon(type)}
                            <span className="text-sm capitalize">{type.replace(/_/g, ' ')}</span>
                          </div>
                          <span className="font-mono font-bold">{count}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-emerald-300" />
                      <p>Aucune alerte sur cette période</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Compliance Summary */}
              <Card className="bg-slate-900 text-white">
                <CardHeader>
                  <CardTitle className="text-white">Résumé conformité</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Transactions analysées</span>
                      <span className="font-mono font-bold">{dashboard?.screenings?.total || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">Taux de risque élevé</span>
                      <span className="font-mono font-bold text-orange-400">{dashboard?.screenings?.high_risk_rate || 0}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">SAR soumis ce mois</span>
                      <span className="font-mono font-bold text-emerald-400">{dashboard?.sars?.submitted_this_period || 0}</span>
                    </div>
                    <hr className="border-slate-700" />
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">SAR en attente</span>
                      <span className={`font-mono font-bold ${(dashboard?.sars?.pending || 0) > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                        {dashboard?.sars?.pending || 0}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* SARs Tab */}
          <TabsContent value="sars" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Rapports d&apos;Activités Suspectes (SAR)</CardTitle>
                <CardDescription>Rapports générés automatiquement nécessitant une revue</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingSars.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-emerald-300" />
                    <p>Aucun SAR en attente</p>
                    <p className="text-sm mt-2">Tous les rapports ont été traités</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingSars.map((sar) => (
                      <div key={sar.id} className="p-4 border rounded-lg hover:bg-slate-50">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              {getRiskBadge(sar.risk_category)}
                              <span className="text-sm text-slate-500">
                                Score: {sar.risk_score}/100
                              </span>
                              <Badge variant="outline">{sar.status}</Badge>
                            </div>
                            <p className="font-medium">
                              Transaction de {sar.amount?.toLocaleString('fr-FR')}€
                            </p>
                            <p className="text-sm text-slate-500 mt-1">
                              Créé le {new Date(sar.created_at).toLocaleDateString('fr-FR')}
                            </p>
                            <p className="text-xs text-amber-600 mt-1">
                              Échéance: {new Date(sar.due_date).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedSar(sar);
                                setShowSarModal(true);
                              }}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Examiner
                            </Button>
                          </div>
                        </div>
                        {sar.flags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {sar.flags.map((flag, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {flag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Thresholds Tab */}
          <TabsContent value="thresholds" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Seuils de surveillance AML</CardTitle>
                <CardDescription>Seuils configurés conformément à la directive européenne LCB-FT</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-amber-800">Seuil transaction unique</p>
                        <p className="text-sm text-amber-600">Déclenche une vigilance renforcée (EDD)</p>
                      </div>
                      <span className="font-mono font-bold text-amber-900 text-xl">
                        {(dashboard?.thresholds?.single_transaction || 10000).toLocaleString('fr-FR')}€
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-orange-800">Seuil cumulé journalier</p>
                        <p className="text-sm text-orange-600">Montant maximum par jour</p>
                      </div>
                      <span className="font-mono font-bold text-orange-900 text-xl">
                        {(dashboard?.thresholds?.daily_cumulative || 15000).toLocaleString('fr-FR')}€
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-red-800">Seuil cumulé mensuel</p>
                        <p className="text-sm text-red-600">Montant maximum par mois</p>
                      </div>
                      <span className="font-mono font-bold text-red-900 text-xl">
                        {(dashboard?.thresholds?.monthly_cumulative || 50000).toLocaleString('fr-FR')}€
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-slate-100 rounded-lg">
                  <p className="text-sm text-slate-600">
                    <strong>Note:</strong> Ces seuils sont conformes à la 6ème directive européenne anti-blanchiment (6AMLD). 
                    Toute transaction dépassant ces seuils fait l&apos;objet d&apos;une analyse automatique et peut générer un SAR.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* SAR Detail Modal */}
        <Dialog open={showSarModal} onOpenChange={setShowSarModal}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Rapport d&apos;Activité Suspecte
              </DialogTitle>
              <DialogDescription>
                ID: {selectedSar?.id}
              </DialogDescription>
            </DialogHeader>
            {selectedSar && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">Montant</p>
                    <p className="font-mono font-bold">{selectedSar.amount?.toLocaleString('fr-FR')}€</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">Score de risque</p>
                    <p className="font-mono font-bold">{selectedSar.risk_score}/100</p>
                  </div>
                </div>
                
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-2">Alertes déclenchées</p>
                  <div className="space-y-2">
                    {selectedSar.alerts?.map((alert, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                        <span>{alert.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600 mb-2">Narrative (auto-générée)</p>
                  <pre className="text-xs whitespace-pre-wrap font-mono bg-white p-3 rounded border max-h-48 overflow-y-auto">
                    {selectedSar.narrative}
                  </pre>
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowSarModal(false)}>
                Fermer
              </Button>
              <Button
                className="bg-emerald-500 hover:bg-emerald-600"
                onClick={() => handleSubmitSar(selectedSar?.id)}
              >
                <Send className="w-4 h-4 mr-2" />
                Soumettre le SAR
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AMLDashboardPage;
